/**
 * Cake Finder — personality quiz + dynamic, rule-based recommendation engine.
 *
 * Reads the JSON island rendered by sections/cake-quiz.liquid:
 *   { config: { questions[], personas[] }, products[] }
 *
 * Flow:  answers -> trait vector -> PERSONA (shareable identity) -> ranked cakes.
 *
 * - QUESTIONS & PERSONAS are authored as theme-editor blocks, so the quiz can be
 *   redesigned (more/fewer/re-ordered questions, new personas) WITHOUT touching
 *   product tags. If no blocks are configured, the built-in DEFAULT_CONFIG below
 *   is used so the section works out of the box.
 * - CAKE MATCHING stays attribute-driven: a cake's trait vector is derived from
 *   its existing quiz-flavour-* / quiz-texture-* / quiz-occasion-* tags. Add a
 *   correctly tagged cake to the collection and it joins the pool automatically.
 * - COHERENCE is preserved: alcohol is a hard filter (family-friendly never
 *   returns an alcoholic cake); flavour/texture/occasion drive a weighted score.
 * - The result returns a PRIMARY + BACKUPS before the "browse collection"
 *   fallback. When data-ai-match-url is set, an optional backend re-ranks the
 *   shortlist and writes a personalised note (falling back to the rule result).
 *
 * Trait vocabulary = your cake tag suffixes (fruity, rich, earthy, nutty, silky,
 * creamy, dense, contrasting, milestone, pickmeup, impress, chill, ...). Use the
 * same words in option "Value" fields and persona "Trait coordinates".
 */

(function () {
  'use strict';

  /* ============================================================= */
  /* DEFAULT_CONFIG — used only when no question blocks exist.      */
  /* Authoring is normally done via theme-editor blocks.           */
  /* ============================================================= */
  const DEFAULT_CONFIG = {
    questions: [
      {
        type: 'trait',
        text: "What's the occasion that calls for a slice of magic?",
        options: [
          { id: 'q1_o1', label: 'Major Milestone', emoji: '🎉', value: 'milestone:2', snippet: '🥚 Sifting flour & breaking eggs... only the finest for your big moment!' },
          { id: 'q1_o2', label: 'A Pick-Me-Up Moment', emoji: '😌', value: 'pickmeup:2', snippet: '🥚 Sifting flour & breaking eggs... a sweet remedy is on its way.' },
          { id: 'q1_o3', label: 'Impress Someone Special', emoji: '🎁', value: 'impress:2', snippet: '🥚 Crafting something extraordinary to leave them speechless.' },
          { id: 'q1_o4', label: 'Chill & Unwind', emoji: '☕', value: 'chill:2', snippet: '🥚 Preparing your perfect comfort treat.' }
        ]
      },
      {
        type: 'trait',
        text: 'Which flavour adventure tickles your taste buds?',
        options: [
          { id: 'q2_o1', label: 'Fruity & Fresh', emoji: '🍓', value: 'fruity:2', snippet: "🍓 Folding in fresh fruit purées... nature's sweetness dancing in!" },
          { id: 'q2_o2', label: 'Rich & Aromatic', emoji: '🍫', value: 'rich:2', snippet: '🍫 Melting chocolate & swirling in decadence...' },
          { id: 'q2_o3', label: 'Earthy & Unique', emoji: '🍵', value: 'earthy:2', snippet: '🍵 Whisking in delicate tea essences...' },
          { id: 'q2_o4', label: 'Nutty & Rich', emoji: '🌰', value: 'nutty:2', snippet: '🌰 Grinding nuts to perfection...' }
        ]
      },
      {
        type: 'trait',
        text: 'What kind of texture makes your heart melt?',
        options: [
          { id: 'q3_o1', label: 'Silky Smooth like Tau Huay', value: 'silky:2', tooltip: 'A soft, silken tofu dessert, often served warm with syrup.', snippet: '🥄 Gently folding the batter... clouds of silk in every layer.' },
          { id: 'q3_o2', label: 'Creamy & Luxurious like Durian Mousse', value: 'creamy:2', tooltip: 'A creamy dessert made from the tropical fruit, durian.', snippet: '🥄 Whipping to creamy perfection...' },
          { id: 'q3_o3', label: 'Dense & Satisfying like Kueh Lapis', value: 'dense:2', tooltip: 'A rich, layered steamed cake known for its dense texture.', snippet: '🥄 Mixing with purpose... layers of rich satisfaction.' },
          { id: 'q3_o4', label: 'Contrasting Textures like Ice Kacang', value: 'contrasting:2', tooltip: 'A shaved-ice dessert topped with syrups, beans and jellies.', snippet: '🥄 Layering textures like an artist... surprises in every bite!' }
        ]
      },
      {
        type: 'alcohol',
        text: 'Can we add alcohol to make it extra special?',
        options: [
          { id: 'q4_o1', label: 'No, keeping it family-friendly', emoji: '👶', value: 'deny', snippet: '✨ Adding family-friendly finishing touches... joy for all ages!' },
          { id: 'q4_o2', label: 'Yes, adults only', emoji: '🥂', value: 'allow', snippet: '✨ Drizzling in grown-up sophistication...' }
        ]
      },
      {
        type: 'size',
        text: 'How many people will share this delight?',
        subtext: '(Serving sizes are guides — share the joy!)',
        options: [
          { id: 'q5_o1', label: 'Intimate (1-4 people)', emoji: '👫', value: '10cm||1-4 pax', snippet: '🔥 Into the oven... your intimate treat is baking to perfection!' },
          { id: 'q5_o2', label: 'Family (5-8 people)', emoji: '👨‍👩‍👧‍👦', value: '16cm|15cm|5-8 pax', snippet: '🔥 Into the oven... your family-sized happiness is rising!' },
          { id: 'q5_o3', label: 'Party (9-12 people)', emoji: '🎉', value: '20cm||9-12 pax', snippet: '🔥 Into the oven... your party centrepiece is taking shape!' },
          { id: 'q5_o4', label: 'Crowd (13+ people)', emoji: '🏢', value: '30cm||13+ pax', snippet: '🔥 Into the oven... a grand creation worthy of the crowd!' }
        ]
      }
    ],
    personas: [
      { slug: 'the-midnight-indulger', name: 'The Midnight Indulger', emoji: '🍫', tagline: 'Rich, bold and unapologetically decadent.', description: "You don't do things by halves. When you treat yourself, you go all in on deep, grown-up flavour.", rarity: 'Only 9% of people are this type', shareText: "I'm The Midnight Indulger 🍫 — what's your Nesuto cake personality?", traits: 'rich:3, nutty:1', cakeHandles: [] },
      { slug: 'the-sunlit-romantic', name: 'The Sunlit Romantic', emoji: '🍓', tagline: 'Bright, fresh and full of heart.', description: 'You love a cake that tastes like a sunny afternoon — fruit-forward, light and joyful.', rarity: 'About 1 in 4 people', shareText: "I'm The Sunlit Romantic 🍓 — what's your Nesuto cake personality?", traits: 'fruity:3, silky:1', cakeHandles: [] },
      { slug: 'the-quiet-connoisseur', name: 'The Quiet Connoisseur', emoji: '🍵', tagline: 'Refined, understated, quietly adventurous.', description: 'You appreciate the subtle and the unexpected — earthy, tea-kissed, grown-up flavours.', rarity: 'A rare 8% of people', shareText: "I'm The Quiet Connoisseur 🍵 — what's your Nesuto cake personality?", traits: 'earthy:3, dense:1', cakeHandles: [] },
      { slug: 'the-playful-maximalist', name: 'The Playful Maximalist', emoji: '🎉', tagline: 'More is more — texture, colour and fun.', description: 'You want every bite to surprise you. The more going on, the happier you are.', rarity: 'Around 1 in 5 people', shareText: "I'm The Playful Maximalist 🎉 — what's your Nesuto cake personality?", traits: 'contrasting:3, fruity:1', cakeHandles: [] }
    ]
  };

  /* Scoring weights. seasonal weight is read from the section setting. */
  const WEIGHT = { trait: 3, hasRequestedSize: 1, pinned: 2 };

  /* ============================================================= */
  /* Helpers                                                        */
  /* ============================================================= */
  const normSize = (s) => String(s == null ? '' : s).toLowerCase().replace(/\s+/g, '');

  function tagSuffixes(tags, prefix) {
    return (tags || [])
      .filter((t) => typeof t === 'string' && t.toLowerCase().indexOf(prefix) === 0)
      .map((t) => t.toLowerCase().slice(prefix.length));
  }

  function parseTraits(str) {
    const v = {};
    if (!str) return v;
    String(str).split(',').forEach((pair) => {
      const bits = pair.split(':');
      const key = (bits[0] || '').trim().toLowerCase();
      if (!key) return;
      const w = bits.length > 1 ? parseFloat(bits[1]) : 1;
      v[key] = (v[key] || 0) + (isNaN(w) ? 1 : w);
    });
    return v;
  }

  function cosineSim(a, b) {
    let dot = 0, na = 0, nb = 0;
    for (const k in a) { na += a[k] * a[k]; if (b[k]) dot += a[k] * b[k]; }
    for (const k in b) { nb += b[k] * b[k]; }
    if (na === 0 || nb === 0) return 0;
    return dot / (Math.sqrt(na) * Math.sqrt(nb));
  }

  function topKeys(vec, n) {
    return Object.keys(vec).sort((a, b) => vec[b] - vec[a]).slice(0, n);
  }

  function slugify(s) {
    return String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  function formatHour(h) {
    const ampm = h >= 12 ? 'PM' : 'AM';
    let hh = h % 12;
    if (hh === 0) hh = 12;
    return hh + ' ' + ampm;
  }

  function escapeHtml(str) {
    const d = document.createElement('div');
    d.textContent = str == null ? '' : String(str);
    return d.innerHTML;
  }

  function asArray(v) {
    if (Array.isArray(v)) return v;
    if (v == null || v === '') return [];
    return String(v).split(',').map((s) => s.trim()).filter(Boolean);
  }

  /* ============================================================= */
  /* Custom element                                                 */
  /* ============================================================= */
  class CakeQuiz extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.config = {
        ga4Id: this.dataset.ga4Id || '',
        cutoffHour: parseInt(this.dataset.cutoffHour, 10) || 14,
        seasonalBoost: parseFloat(this.dataset.seasonalBoost),
        browseUrl: this.dataset.browseUrl || '/collections/all',
        quizUrl: this.dataset.quizUrl || (window.location.pathname || '/'),
        aiMatchUrl: this.dataset.aiMatchUrl || '',
        logo: this.dataset.logo || '',
        introTitle: this.dataset.introTitle || 'Find Your Perfect Cake',
        introText: this.dataset.introText || '',
        startLabel: this.dataset.startLabel || "Let's Start!",
        designMode: this.dataset.designMode === 'true'
      };
      if (isNaN(this.config.seasonalBoost)) this.config.seasonalBoost = 0.5;

      const island = this.parseIsland();
      this.products = this.parseProducts(island.products);
      const cfg = island.config || {};
      this.questions = (cfg.questions && cfg.questions.length) ? cfg.questions : DEFAULT_CONFIG.questions;
      this.personas = (cfg.personas && cfg.personas.length) ? cfg.personas : DEFAULT_CONFIG.personas;
      this.totalQuestions = this.questions.length;

      // Editor-only setup check (rendered as a sibling so innerHTML rebuilds never wipe it).
      if (this.config.designMode) { try { this.renderDiagnostics(); } catch (e) { /* no-op */ } }

      this.answers = {};
      this.quizStartTime = null;
      this.questionStartTimes = {};

      // Inbound shared link: /pages/cake-quiz?persona=the-sunlit-romantic
      try {
        const slug = new URLSearchParams(window.location.search).get('persona');
        this.incomingPersona = slug ? this.personas.find((p) => p.slug === slug) : null;
      } catch (e) { this.incomingPersona = null; }

      if (!this.products.length || !this.totalQuestions) {
        this.renderEmptyState();
        return;
      }

      this.buildHtml();
      this.bindEvents();
      this.initReferralTracking();
      if (this.incomingPersona) {
        this.sendGA4('deep_link_landing', { persona: this.incomingPersona.slug });
      }
    }

    /* ---- Data ---- */
    parseIsland() {
      const tag = this.querySelector('.cake-quiz__data');
      if (!tag) return { config: {}, products: [] };
      try {
        return JSON.parse(tag.textContent);
      } catch (e) {
        console.error('[cake-quiz] Could not parse island data:', e);
        return { config: {}, products: [] };
      }
    }

    parseProducts(list) {
      return (list || []).map((p) => {
        const tags = p.tags || [];
        let availSizes = (p.sizes || [])
          .filter((s) => s.available)
          .map((s) => ({ name: s.name, norm: normSize(s.name), price: s.price, variantId: s.variant_id }));
        if (!availSizes.length) {
          availSizes = [{ name: '', norm: '', price: p.price, variantId: 0 }];
        }
        const flavours = tagSuffixes(tags, 'quiz-flavour-');
        const textures = tagSuffixes(tags, 'quiz-texture-');
        const occasions = tagSuffixes(tags, 'quiz-occasion-');
        const traits = {};
        flavours.concat(textures, occasions).forEach((k) => { traits[k] = (traits[k] || 0) + 1; });
        if (!flavours.length && !textures.length && !occasions.length) {
          console.warn('[cake-quiz] "' + p.name + '" has no quiz-* tags, so it will rarely be recommended.');
        }
        const lc = tags.map(String).map((t) => t.toLowerCase());
        // Active seasons this cake belongs to (rendered by the section from the Season blocks).
        const seasons = (p.seasons || []).filter((s) => s && (s.name || s.badge));
        let seasonBoost = 0;
        seasons.forEach((s) => { const b = parseFloat(s.boost) || 0; if (b > seasonBoost) seasonBoost = b; });
        const seasonBadges = seasons
          .map((s) => (s.badge && String(s.badge).trim()) || s.name)
          .filter(Boolean);
        return {
          id: p.id,
          name: p.name,
          handle: p.handle || '',
          url: p.url,
          desc: p.desc || '',
          images: (p.images || []).filter(Boolean),
          price: p.price || 0,
          leadTimeDays: parseInt(p.lead_time_days, 10) || 0,
          traits: traits,
          alcohol: lc.indexOf('quiz-alcohol') !== -1,
          seasonal: lc.indexOf('quiz-seasonal') !== -1,
          leadWeek30: lc.indexOf('quiz-30cm-leadweek') !== -1,
          seasons: seasons,
          seasonBoost: seasonBoost,
          seasonBadges: seasonBadges,
          availSizes: availSizes
        };
      });
    }

    /* ---- Engine ---- */
    optionFor(qIndex, optId) {
      const q = this.questions[qIndex];
      if (!q) return null;
      return (q.options || []).find((o) => o.id === optId) || null;
    }

    // Aggregate the answers into a trait vector + alcohol/size decisions.
    deriveState() {
      const userVector = {};
      let allowAlcohol = null; // null = no alcohol question asked
      let sizeCfg = null;
      this.questions.forEach((q, idx) => {
        const opt = this.answers[idx + 1];
        if (!opt) return;
        if (q.type === 'alcohol') {
          allowAlcohol = String(opt.value || '').trim().toLowerCase() === 'allow';
        } else if (q.type === 'size') {
          const parts = String(opt.value || '').split('|');
          sizeCfg = {
            primary: (parts[0] || '').trim(),
            secondary: (parts[1] || '').trim() || null,
            paxText: (parts[2] || '').trim()
          };
        } else {
          const tv = parseTraits(opt.value);
          for (const k in tv) userVector[k] = (userVector[k] || 0) + tv[k];
        }
      });
      if (allowAlcohol === null) allowAlcohol = true; // no alcohol question => no restriction
      return { userVector, allowAlcohol, sizeCfg };
    }

    pickPersona(userVector) {
      if (!this.personas.length) return null;
      let best = null, bestScore = -1;
      this.personas.forEach((p) => {
        const score = cosineSim(userVector, parseTraits(p.traits));
        if (score > bestScore) { bestScore = score; best = p; }
      });
      // If answers gave no usable overlap, fall back to the first persona.
      return best || this.personas[0];
    }

    hasSize(cake, target) {
      return cake.availSizes.some((s) => s.norm === normSize(target));
    }

    resolveSize(cake, sizeCfg) {
      if (!sizeCfg) return cake.availSizes[0];
      const pick = (t) => cake.availSizes.find((s) => s.norm === normSize(t));
      return pick(sizeCfg.primary) || (sizeCfg.secondary && pick(sizeCfg.secondary)) || cake.availSizes[0];
    }

    rankCakes(pool, userVector, sizeCfg, persona) {
      const pins = persona ? asArray(persona.cakeHandles).map((h) => slugify(h)) : [];
      pool.forEach((cake) => {
        const cos = cosineSim(userVector, cake.traits);
        const pinned = pins.length > 0 && pins.indexOf(slugify(cake.handle)) !== -1;
        let score = cos * WEIGHT.trait;
        score += cake.seasonBoost || 0; // per-season boost from active Season blocks
        if (cake.seasonal) score += this.config.seasonalBoost; // legacy quiz-seasonal tag (back-compat)
        if (pinned) score += WEIGHT.pinned;
        cake._score = score;
        // Only cakes that actually share a trait (or are merchant-pinned) are recommendable.
        cake._eligible = cos > 0 || pinned;
        // A matching cake that also stocks the requested size (only relevant once a size is chosen).
        cake._sizePriority = !!(sizeCfg && cos > 0 && this.hasSize(cake, sizeCfg.primary));
      });
      return pool.slice().sort((a, b) => {
        // 1. matching cakes that stock the requested size come first
        if (a._sizePriority !== b._sizePriority) return a._sizePriority ? -1 : 1;
        // 2. best fit (flavour/texture/occasion + season + pinned)
        if (b._score !== a._score) return b._score - a._score;
        // 3. deterministic, sensible tie-breakers
        if (a.price !== b.price) return a.price - b.price;
        return a.name.localeCompare(b.name);
      });
    }

    match() {
      const state = this.deriveState();
      const persona = this.pickPersona(state.userVector);

      let pool = this.products.slice();
      if (!state.allowAlcohol) pool = pool.filter((p) => !p.alcohol); // hard filter (coherence)

      const ranked = this.rankCakes(pool, state.userVector, state.sizeCfg, persona).filter((c) => c._eligible);
      const toPick = (cake) => ({ cake: cake, size: this.resolveSize(cake, state.sizeCfg), rationale: '' });
      const MORE_CAP = 6;

      return {
        fallback: ranked.length === 0,
        persona: persona,
        sizeCfg: state.sizeCfg,
        userVector: state.userVector,
        allowAlcohol: state.allowAlcohol,
        primary: ranked[0] ? toPick(ranked[0]) : null,
        backups: ranked.slice(1, 3).map(toPick),
        more: ranked.slice(3, 3 + MORE_CAP).map(toPick)
      };
    }

    leadTimeNote(cake, sizeObj) {
      if (cake.leadTimeDays > 0) {
        const n = cake.leadTimeDays;
        return 'Please note: requires ' + n + ' day' + (n > 1 ? 's' : '') + "' advance order.";
      }
      if (sizeObj && normSize(sizeObj.name) === '30cm' && cake.leadWeek30) {
        return "Please note: the 30cm size requires about 1 week's advance order.";
      }
      const nowSGT = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Singapore' }));
      const cutoff = this.config.cutoffHour;
      return nowSGT.getHours() < cutoff
        ? 'Order by ' + formatHour(cutoff) + ' SGT for next-day delivery.'
        : 'Order now for delivery the day after tomorrow.';
    }

    /* ---- HTML build ---- */
    buildHtml() {
      const c = this.config;
      let steps = '';

      this.questions.forEach((q, idx) => {
        const qn = idx + 1;
        let opts = '';
        (q.options || []).forEach((opt) => {
          const emoji = opt.emoji ? escapeHtml(opt.emoji) + ' ' : '';
          // The info trigger is a SIBLING of the button (not nested inside it), so tapping
          // "?" toggles the tooltip without selecting the answer / advancing the quiz.
          const tip = opt.tooltip
            ? '<span class="cake-quiz__info" data-tooltip="' + escapeHtml(opt.tooltip) + '" tabindex="0" role="button" aria-label="More info: ' + escapeHtml(opt.tooltip) + '">?</span>'
            : '';
          opts +=
            '<div class="cake-quiz__option-row">' +
              '<button type="button" class="cake-quiz__option" data-q="' + qn + '" data-opt="' + escapeHtml(opt.id) + '">' +
                '<span>' + emoji + escapeHtml(opt.label) + '</span>' +
              '</button>' +
              tip +
            '</div>';
        });
        steps +=
          '<div class="cake-quiz__step cake-quiz__question" data-step="q' + qn + '">' +
          '<div class="cake-quiz__content">' +
          '<h3 class="cake-quiz__qtitle"><span>' + qn + '/' + this.totalQuestions + ':</span> ' + escapeHtml(q.text) + '</h3>' +
          (q.subtext ? '<p class="cake-quiz__subtext">' + escapeHtml(q.subtext) + '</p>' : '') +
          '<div class="cake-quiz__options">' + opts + '</div>' +
          '</div></div>';
        steps +=
          '<div class="cake-quiz__step cake-quiz__snippet" data-step="snippet' + qn + '">' +
          '<div class="cake-quiz__content">' +
          '<p class="cake-quiz__snippet-text" data-snippet="' + qn + '">Mixing the perfect blend...</p>' +
          '<button type="button" class="cake-quiz__btn" data-action="proceed" data-q="' + qn + '">Continue</button>' +
          '</div></div>';
      });

      let introExtra = '';
      if (this.incomingPersona) {
        introExtra =
          '<p class="cake-quiz__teaser">Your friend got <strong>' +
          escapeHtml((this.incomingPersona.emoji ? this.incomingPersona.emoji + ' ' : '') + this.incomingPersona.name) +
          '</strong> — what\'s your cake personality?</p>';
      }

      this.innerHTML =
        '<div class="cake-quiz__inner">' +
          '<div class="cake-quiz__step is-active" data-step="intro">' +
            '<div class="cake-quiz__content">' +
              (c.logo ? '<img src="' + escapeHtml(c.logo) + '" alt="" class="cake-quiz__logo">' : '') +
              '<h2 class="cake-quiz__title">' + escapeHtml(c.introTitle) + '</h2>' +
              introExtra +
              (c.introText ? '<p>' + escapeHtml(c.introText) + '</p>' : '') +
              '<button type="button" class="cake-quiz__btn" data-action="start">' + escapeHtml(c.startLabel) + '</button>' +
            '</div>' +
          '</div>' +
          '<div class="cake-quiz__progress" hidden>' +
            '<div class="cake-quiz__progress-bar"><div class="cake-quiz__progress-fill"></div></div>' +
            '<div class="cake-quiz__progress-text">Getting started...</div>' +
          '</div>' +
          '<div class="cake-quiz__steps">' + steps + '</div>' +
          '<div class="cake-quiz__result" data-step="result" hidden></div>' +
        '</div>';

      this.els = {
        steps: this.querySelectorAll('.cake-quiz__step'),
        progress: this.querySelector('.cake-quiz__progress'),
        progressFill: this.querySelector('.cake-quiz__progress-fill'),
        progressText: this.querySelector('.cake-quiz__progress-text'),
        result: this.querySelector('.cake-quiz__result')
      };
    }

    renderEmptyState() {
      if (this.config && this.config.designMode) {
        this.innerHTML = '<div class="cake-quiz__inner"><p class="cake-quiz__notice">Add a collection plus Question and Persona blocks (or rely on the built-in defaults) and tag products (quiz-flavour-*, etc.) to power the Cake Finder.</p></div>';
      }
    }

    // Theme-editor-only panel: surfaces the live trait/size vocabulary, active
    // seasons, and anything a question references that no available cake provides.
    renderDiagnostics() {
      const products = this.products || [];
      const esc = escapeHtml;
      const families = {
        Flavour: ['fruity', 'rich', 'earthy', 'nutty'],
        Texture: ['silky', 'creamy', 'dense', 'contrasting'],
        Occasion: ['milestone', 'pickmeup', 'impress', 'chill']
      };
      const cakesWith = (w) => products.reduce((n, c) => n + (c.traits[w] ? 1 : 0), 0);
      const hasSizeAnywhere = (sz) => products.some((c) => c.availSizes.some((s) => s.norm === normSize(sz)));

      // What the questions actually reference.
      const usedTraits = {};
      const usedSizes = {};
      (this.questions || []).forEach((q) => {
        (q.options || []).forEach((o) => {
          if (q.type === 'trait') {
            Object.keys(parseTraits(o.value)).forEach((k) => { usedTraits[k] = true; });
          } else if (q.type === 'size') {
            String(o.value || '').split('|').slice(0, 2).forEach((s) => { if (s.trim()) usedSizes[s.trim()] = true; });
          }
        });
      });

      // Sizes & seasons present in the pool.
      const sizeSet = {};
      const seasonCount = {};
      products.forEach((c) => {
        c.availSizes.forEach((s) => { if (s.name) sizeSet[s.name] = true; });
        (c.seasons || []).forEach((s) => { if (s.name) seasonCount[s.name] = (seasonCount[s.name] || 0) + 1; });
      });
      const untagged = products.filter((c) => !Object.keys(c.traits).length).map((c) => c.name);

      // Warnings.
      const warnings = [];
      if (!products.length) warnings.push('No available cakes in the pool — check the collection and Season blocks.');
      Object.keys(usedTraits).forEach((t) => { if (!cakesWith(t)) warnings.push('Question trait “' + t + '” matches 0 available cakes.'); });
      Object.keys(usedSizes).forEach((sz) => { if (!hasSizeAnywhere(sz)) warnings.push('Requested size “' + sz + '” isn’t available on any cake.'); });
      if (untagged.length) warnings.push(untagged.length + ' cake(s) have no quiz-* trait tags: ' + untagged.join(', '));

      const famHtml = Object.keys(families).map((fam) =>
        '<div class="cake-quiz__diag-row"><strong>' + fam + ':</strong> ' +
        families[fam].map((w) => '<span class="cake-quiz__diag-chip' + (cakesWith(w) ? '' : ' is-zero') + '">' + w + ' (' + cakesWith(w) + ')</span>').join(' ') +
        '</div>').join('');
      const sizesHtml = Object.keys(sizeSet).length ? Object.keys(sizeSet).map(esc).join(', ') : '<em>none detected</em>';
      const seasonsHtml = Object.keys(seasonCount).length
        ? Object.keys(seasonCount).map((s) => esc(s) + ' (' + seasonCount[s] + ')').join(', ')
        : '<em>no active seasons</em>';
      const warnHtml = warnings.length
        ? '<ul class="cake-quiz__diag-warn">' + warnings.map((w) => '<li>⚠ ' + esc(w) + '</li>').join('') + '</ul>'
        : '<p class="cake-quiz__diag-ok">✓ No problems detected.</p>';

      const panel = document.createElement('div');
      panel.className = 'cake-quiz__diag';
      panel.innerHTML =
        '<p class="cake-quiz__diag-title">🛠 Cake Finder setup check <span>(visible only to you in the theme editor)</span></p>' +
        '<div class="cake-quiz__diag-row"><strong>Pool:</strong> ' + products.length + ' available cake(s)</div>' +
        famHtml +
        '<div class="cake-quiz__diag-row"><strong>Sizes:</strong> ' + sizesHtml + '</div>' +
        '<div class="cake-quiz__diag-row"><strong>Active seasons:</strong> ' + seasonsHtml + '</div>' +
        warnHtml;

      if (this.parentNode) this.parentNode.insertBefore(panel, this);
      else this.appendChild(panel);
    }

    bindEvents() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('.cake-quiz__info')) return; // tooltip trigger — let the toggle handler take it
        const opt = e.target.closest('.cake-quiz__option');
        if (opt) {
          this.selectOption(parseInt(opt.dataset.q, 10), opt.dataset.opt);
          return;
        }
        const btn = e.target.closest('[data-action]');
        if (!btn) return;
        const action = btn.dataset.action;
        if (action === 'start') this.start();
        else if (action === 'proceed') this.proceedFromSnippet(parseInt(btn.dataset.q, 10));
        else if (action === 'restart') this.restart();
        else if (action === 'more') this.toggleMore(btn);
        else if (action === 'share') this.shareResult();
      });

      // Tap-to-toggle tooltips on touch devices.
      this.addEventListener('click', (e) => {
        const info = e.target.closest('.cake-quiz__info');
        this.querySelectorAll('.cake-quiz__info.is-open').forEach((el) => {
          if (el !== info) el.classList.remove('is-open');
        });
        if (info) {
          e.preventDefault();
          e.stopPropagation();
          info.classList.toggle('is-open');
        }
      });
    }

    /* ---- Flow ---- */
    showStep(name) {
      this.els.steps.forEach((s) => s.classList.toggle('is-active', s.dataset.step === name));
      if (name === 'result') {
        this.els.result.hidden = false;
        this.els.steps.forEach((s) => s.classList.remove('is-active'));
      }
      try {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) { /* no-op */ }
    }

    start() {
      this.answers = {};
      this.quizStartTime = Date.now();
      this.questionStartTimes[1] = Date.now();
      this.els.progress.hidden = false;
      this.sendGA4('quiz_started', { engagement_time_msec: 100, session_engaged: true });
      this.showStep('q1');
      this.updateProgress();
    }

    selectOption(qNumber, optId) {
      const opt = this.optionFor(qNumber - 1, optId);
      if (!opt) return;
      const started = this.questionStartTimes[qNumber] || Date.now();
      const timeSpent = Date.now() - started;
      this.answers[qNumber] = opt;

      this.sendGA4('quiz_answered', {
        question_number: qNumber,
        answer_selected: opt.label,
        question_id: 'q' + qNumber,
        time_spent_seconds: Math.round(timeSpent / 1000),
        engagement_time_msec: timeSpent
      });

      const snippetText = this.querySelector('[data-snippet="' + qNumber + '"]');
      if (snippetText) snippetText.textContent = opt.snippet || 'Mixing the perfect blend...';
      this.showStep('snippet' + qNumber);
      this.updateProgress(true);
    }

    proceedFromSnippet(qNumber) {
      if (qNumber < this.totalQuestions) {
        const next = qNumber + 1;
        this.questionStartTimes[next] = Date.now();
        this.showStep('q' + next);
        this.updateProgress();
      } else {
        this.showResult();
      }
    }

    updateProgress(mixing) {
      const answered = Object.keys(this.answers).length;
      const pct = (answered / this.totalQuestions) * 100;
      if (this.els.progressFill) this.els.progressFill.style.width = pct + '%';
      if (this.els.progressText) {
        this.els.progressText.textContent = mixing
          ? 'Mixing ingredients...'
          : answered >= this.totalQuestions
            ? 'Finding your match...'
            : 'Question ' + Math.min(answered + 1, this.totalQuestions) + ' of ' + this.totalQuestions;
      }
    }

    /* ---- Result ---- */
    showResult() {
      const result = this.match();
      this.lastResult = result;
      const totalTime = this.quizStartTime ? Date.now() - this.quizStartTime : 0;
      const persona = result.persona;

      this.sendGA4('persona_revealed', {
        persona: persona ? persona.slug : 'none',
        persona_name: persona ? persona.name : 'None',
        top_traits: topKeys(result.userVector, 3).join(',')
      });
      this.sendGA4('quiz_completed', {
        persona: persona ? persona.slug : 'none',
        recommended_cake_id: result.fallback ? 'none' : result.primary.cake.id,
        recommended_cake_name: result.fallback ? 'None' : result.primary.cake.name,
        backup_cake_ids: result.backups.map((b) => b.cake.id).join(','),
        recommended_size: result.fallback || !result.primary.size ? 'N/A' : result.primary.size.name,
        total_time_seconds: Math.round(totalTime / 1000),
        questions_answered: Object.keys(this.answers).length
      });
      if (!result.fallback) {
        const val = (result.primary.size ? result.primary.size.price : result.primary.cake.price) / 100;
        this.sendGA4('generate_lead', { currency: 'SGD', value: val, lead_type: 'quiz_completion' });
      }

      if (this.els.progress) this.els.progress.hidden = true;
      if (result.fallback) this.renderResult(result, true);
      else this.renderResult(result, false);
      this.showStep('result');

      if (!result.fallback && this.config.aiMatchUrl) this.maybeEnhanceWithAI(result);
    }

    personaHeaderHtml(persona) {
      if (!persona) return '';
      const img = persona.image
        ? '<img class="cake-quiz__persona-img" src="' + escapeHtml(persona.image) + '" alt="">'
        : (persona.emoji ? '<div class="cake-quiz__persona-emoji">' + escapeHtml(persona.emoji) + '</div>' : '');
      return (
        '<div class="cake-quiz__persona">' +
          img +
          '<p class="cake-quiz__persona-kicker">Your cake personality</p>' +
          '<h2 class="cake-quiz__persona-name">' + escapeHtml(persona.name) + '</h2>' +
          (persona.tagline ? '<p class="cake-quiz__persona-tagline">' + escapeHtml(persona.tagline) + '</p>' : '') +
          (persona.description ? '<p class="cake-quiz__persona-desc">' + escapeHtml(persona.description) + '</p>' : '') +
          (persona.rarity ? '<p class="cake-quiz__persona-rarity">' + escapeHtml(persona.rarity) + '</p>' : '') +
          '<button type="button" class="cake-quiz__btn cake-quiz__btn--share" data-action="share">Share my result</button>' +
        '</div>'
      );
    }

    seasonBadgesHtml(cake) {
      if (!cake.seasonBadges || !cake.seasonBadges.length) return '';
      return '<div class="cake-quiz__badges">' +
        cake.seasonBadges.map((b) => '<span class="cake-quiz__badge">' + escapeHtml(b) + '</span>').join('') +
        '</div>';
    }

    primaryCakeHtml(item, sizeCfg) {
      const cake = item.cake;
      const sizeObj = item.size;
      const sizeName = sizeObj && sizeObj.name ? sizeObj.name : '';
      let sizeNote;
      if (sizeName && sizeCfg && normSize(sizeName) === normSize(sizeCfg.primary)) {
        sizeNote = 'Perfect! ' + cake.name + ' comes in ' + sizeName + ' — ideal for ' + sizeCfg.paxText + '.';
      } else if (sizeName) {
        sizeNote = cake.name + ' is available in the ' + sizeName + ' size.';
      } else {
        sizeNote = cake.name + ' is your top match!';
      }
      let lead = this.leadTimeNote(cake, sizeObj);
      if (cake.alcohol) lead += (lead ? ' ' : '') + 'Contains alcohol.';

      const images = cake.images.length ? cake.images : [''];
      const thumbs = images.length > 1
        ? '<div class="cake-quiz__thumbs">' + images.map((src, i) =>
            '<button type="button" class="cake-quiz__thumb' + (i === 0 ? ' is-active' : '') + '" data-img="' + escapeHtml(src) + '">' +
            '<img src="' + escapeHtml(src) + '" alt="" loading="lazy"></button>').join('') + '</div>'
        : '';

      return (
        '<div class="cake-quiz__result-grid">' +
          '<div class="cake-quiz__media">' +
            '<img class="cake-quiz__main-img" src="' + escapeHtml(images[0]) + '" alt="' + escapeHtml(cake.name) + '">' +
            thumbs +
          '</div>' +
          '<div class="cake-quiz__details">' +
            '<h3 class="cake-quiz__cake-name">' + escapeHtml(cake.name) + '</h3>' +
            this.seasonBadgesHtml(cake) +
            '<p class="cake-quiz__size-note">' + escapeHtml(sizeNote) + '</p>' +
            '<p class="cake-quiz__rationale" data-rationale="primary">' + escapeHtml(item.rationale || '') + '</p>' +
            '<p class="cake-quiz__desc">' + escapeHtml(cake.desc) + '</p>' +
            '<p class="cake-quiz__lead">' + escapeHtml(lead) + '</p>' +
            '<a href="' + escapeHtml(cake.url) + '" class="cake-quiz__btn cake-quiz__btn--shop" data-shop="primary" target="_blank" rel="noopener">Shop ' + escapeHtml(cake.name) + '</a>' +
          '</div>' +
        '</div>'
      );
    }

    cakeCardHtml(item, key, withRationale) {
      const cake = item.cake;
      const img = cake.images.length ? cake.images[0] : '';
      const sizeName = item.size && item.size.name ? item.size.name : '';
      return (
        '<div class="cake-quiz__backup">' +
          (img ? '<img class="cake-quiz__backup-img" src="' + escapeHtml(img) + '" alt="" loading="lazy">' : '') +
          '<div class="cake-quiz__backup-body">' +
            '<h4 class="cake-quiz__backup-name">' + escapeHtml(cake.name) + (sizeName ? ' <span>(' + escapeHtml(sizeName) + ')</span>' : '') + '</h4>' +
            this.seasonBadgesHtml(cake) +
            (withRationale ? '<p class="cake-quiz__rationale" data-rationale="' + key + '"></p>' : '') +
            '<a href="' + escapeHtml(cake.url) + '" class="cake-quiz__backup-link" data-shop="' + key + '" target="_blank" rel="noopener">View cake</a>' +
          '</div>' +
        '</div>'
      );
    }

    backupsHtml(backups) {
      if (!backups.length) return '';
      const cards = backups.map((item, i) => this.cakeCardHtml(item, 'backup-' + i, true)).join('');
      return '<div class="cake-quiz__backups"><p class="cake-quiz__backups-title">You might also love</p>' + cards + '</div>';
    }

    moreHtml(more) {
      if (!more || !more.length) return '';
      const cards = more.map((item, i) => this.cakeCardHtml(item, 'more-' + i, false)).join('');
      return (
        '<div class="cake-quiz__more">' +
          '<button type="button" class="cake-quiz__btn cake-quiz__btn--ghost cake-quiz__more-toggle" data-action="more" aria-expanded="false">More you\'ll love</button>' +
          '<div class="cake-quiz__more-list" hidden>' + cards + '</div>' +
        '</div>'
      );
    }

    toggleMore(btn) {
      const list = this.els.result.querySelector('.cake-quiz__more-list');
      if (!list) return;
      if (list.hasAttribute('hidden')) {
        list.removeAttribute('hidden');
        btn.setAttribute('aria-expanded', 'true');
        btn.textContent = 'Show fewer';
        if (!this._moreTracked) {
          this._moreTracked = true;
          this.sendGA4('view_more_matches', { shown: list.querySelectorAll('.cake-quiz__backup').length });
        }
      } else {
        list.setAttribute('hidden', '');
        btn.setAttribute('aria-expanded', 'false');
        btn.textContent = "More you'll love";
      }
    }

    renderResult(r, isFallback) {
      const persona = r.persona;
      this._moreTracked = false;
      let body;
      if (isFallback) {
        body =
          '<p class="cake-quiz__desc">We couldn\'t find an in-stock match for your taste right now, but your personality says there\'s plenty to explore.</p>' +
          '<div class="cake-quiz__actions">' +
            '<a href="' + escapeHtml(this.config.browseUrl) + '" class="cake-quiz__btn cake-quiz__btn--shop">Browse the Collection</a>' +
            '<button type="button" class="cake-quiz__btn cake-quiz__btn--ghost" data-action="restart">Try Again</button>' +
          '</div>';
      } else {
        body =
          this.primaryCakeHtml(r.primary, r.sizeCfg) +
          this.backupsHtml(r.backups) +
          this.moreHtml(r.more) +
          '<div class="cake-quiz__actions">' +
            '<a href="' + escapeHtml(this.config.browseUrl) + '" class="cake-quiz__btn cake-quiz__btn--ghost">Browse all cakes</a>' +
            '<button type="button" class="cake-quiz__btn cake-quiz__btn--ghost" data-action="restart">Bake Another</button>' +
          '</div>';
      }

      this.els.result.innerHTML =
        '<div class="cake-quiz__content cake-quiz__result-content">' +
          this.personaHeaderHtml(persona) +
          (isFallback ? '' : '<h3 class="cake-quiz__result-heading">Your perfect match 🎉</h3>') +
          body +
        '</div>';

      // Thumbnail swapping.
      const mainImg = this.els.result.querySelector('.cake-quiz__main-img');
      this.els.result.querySelectorAll('.cake-quiz__thumb').forEach((t) => {
        t.addEventListener('click', () => {
          if (mainImg) mainImg.src = t.dataset.img;
          this.els.result.querySelectorAll('.cake-quiz__thumb').forEach((x) => x.classList.remove('is-active'));
          t.classList.add('is-active');
        });
      });

      // Shop-click tracking.
      if (!isFallback) {
        const primary = this.els.result.querySelector('[data-shop="primary"]');
        if (primary) primary.addEventListener('click', () => this.trackShop(r.primary.cake, r.primary.size, 'primary'));
        r.backups.forEach((item, i) => {
          const el = this.els.result.querySelector('[data-shop="backup-' + i + '"]');
          if (el) el.addEventListener('click', () => this.trackShop(item.cake, item.size, 'backup'));
        });
        (r.more || []).forEach((item, i) => {
          const el = this.els.result.querySelector('[data-shop="more-' + i + '"]');
          if (el) el.addEventListener('click', () => this.trackShop(item.cake, item.size, 'more'));
        });
      }
    }

    /* ---- Sharing ---- */
    deepLink(persona) {
      const base = window.location.origin + this.config.quizUrl;
      if (!persona || !persona.slug) return base;
      return base + (base.indexOf('?') === -1 ? '?' : '&') + 'persona=' + encodeURIComponent(persona.slug);
    }

    buildShareCanvas(persona) {
      const W = 1080, H = 1080;
      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.fillStyle = '#f6ebdb'; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#a4715c'; ctx.fillRect(0, 0, W, 16);
      ctx.fillStyle = '#a4715c'; ctx.fillRect(0, H - 16, W, 16);
      ctx.textAlign = 'center';
      ctx.fillStyle = '#8a5d4a';
      ctx.font = '600 40px Georgia, serif';
      ctx.fillText('MY NESUTO CAKE PERSONALITY', W / 2, 220);
      ctx.font = '200px serif';
      ctx.fillText(persona && persona.emoji ? persona.emoji : '🎂', W / 2, 480);
      ctx.fillStyle = '#333';
      ctx.font = '700 80px Georgia, serif';
      this._wrapText(ctx, persona ? persona.name : 'Cake Lover', W / 2, 620, W - 160, 90);
      ctx.fillStyle = '#666';
      ctx.font = '400 42px Georgia, serif';
      this._wrapText(ctx, persona && persona.tagline ? persona.tagline : '', W / 2, 800, W - 200, 56);
      ctx.fillStyle = '#a4715c';
      ctx.font = '600 38px Georgia, serif';
      ctx.fillText('dear-nesuto.com  ·  Take the quiz', W / 2, H - 90);
      return canvas;
    }

    _wrapText(ctx, text, x, y, maxWidth, lineHeight) {
      const words = String(text || '').split(' ');
      let line = '', yy = y;
      for (let i = 0; i < words.length; i++) {
        const test = line + words[i] + ' ';
        if (ctx.measureText(test).width > maxWidth && i > 0) {
          ctx.fillText(line.trim(), x, yy);
          line = words[i] + ' ';
          yy += lineHeight;
        } else {
          line = test;
        }
      }
      ctx.fillText(line.trim(), x, yy);
    }

    shareResult() {
      const persona = this.lastResult ? this.lastResult.persona : null;
      const url = this.deepLink(persona);
      const text = (persona && persona.shareText) || ('I just found my Nesuto cake personality — what\'s yours?');
      this.sendGA4('share_clicked', { persona: persona ? persona.slug : 'none' });

      const canvas = this.buildShareCanvas(persona);
      const finish = (method) => this.sendGA4('share_completed', { persona: persona ? persona.slug : 'none', method: method });

      const tryFileShare = () => new Promise((resolve) => {
        if (!canvas || !navigator.canShare) return resolve(false);
        canvas.toBlob((blob) => {
          if (!blob) return resolve(false);
          const file = new File([blob], 'nesuto-cake-personality.png', { type: 'image/png' });
          if (!navigator.canShare({ files: [file] })) return resolve(false);
          navigator.share({ files: [file], text: text, url: url })
            .then(() => { finish('web_share_file'); resolve(true); })
            .catch(() => resolve(false));
        }, 'image/png');
      });

      tryFileShare().then((done) => {
        if (done) return;
        if (navigator.share) {
          navigator.share({ text: text, url: url })
            .then(() => finish('web_share_link'))
            .catch(() => this._fallbackShare(canvas, url, finish));
          return;
        }
        this._fallbackShare(canvas, url, finish);
      });
    }

    _fallbackShare(canvas, url, finish) {
      // Download the card (if any) and copy the deep link.
      if (canvas) {
        try {
          const a = document.createElement('a');
          a.href = canvas.toDataURL('image/png');
          a.download = 'nesuto-cake-personality.png';
          a.click();
        } catch (e) { /* no-op */ }
      }
      const done = (m) => { finish(m); this._toast('Link copied — share your cake personality!'); };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => done('copy_link')).catch(() => done('download_only'));
      } else {
        done('download_only');
      }
    }

    _toast(msg) {
      let t = this.querySelector('.cake-quiz__toast');
      if (!t) {
        t = document.createElement('div');
        t.className = 'cake-quiz__toast';
        this.querySelector('.cake-quiz__inner').appendChild(t);
      }
      t.textContent = msg;
      t.classList.add('is-visible');
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => t.classList.remove('is-visible'), 2600);
    }

    restart() {
      this.sendGA4('quiz_restarted', { restart_time: this.quizStartTime ? Date.now() - this.quizStartTime : 0 });
      this.answers = {};
      this.els.result.hidden = true;
      this.els.result.innerHTML = '';
      this.els.progress.hidden = true;
      this.updateProgress();
      this.showStep('intro');
    }

    /* ---- Optional AI re-rank (Phase 2; dormant unless data-ai-match-url set) ---- */
    maybeEnhanceWithAI(result) {
      const payload = {
        persona: result.persona ? result.persona.slug : null,
        traits: result.userVector,
        allowAlcohol: result.allowAlcohol,
        sizeCfg: result.sizeCfg,
        answers: Object.keys(this.answers).map((k) => ({ q: k, label: this.answers[k].label, value: this.answers[k].value })),
        candidates: [result.primary].concat(result.backups).map((p) => p.cake.id)
      };
      const controller = ('AbortController' in window) ? new AbortController() : null;
      const timer = setTimeout(() => { if (controller) controller.abort(); }, 6000);
      fetch(this.config.aiMatchUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('bad status'))))
        .then((data) => {
          clearTimeout(timer);
          this.applyAIResult(result, data);
          this.sendGA4('ai_match_used', { persona: payload.persona });
        })
        .catch(() => {
          clearTimeout(timer);
          this.sendGA4('ai_match_fallback', { persona: payload.persona });
        });
    }

    applyAIResult(result, data) {
      if (!data || typeof data !== 'object') return;
      const rationales = data.rationales || {};
      const all = [result.primary].concat(result.backups).filter(Boolean);
      const setRationale = (sel, text) => {
        const el = this.els.result.querySelector(sel);
        if (el && text) el.textContent = text;
      };
      // Annotate only — never reorder into something the rule engine excluded.
      if (result.primary && rationales[result.primary.cake.id]) {
        setRationale('[data-rationale="primary"]', rationales[result.primary.cake.id]);
      }
      result.backups.forEach((b, i) => {
        if (rationales[b.cake.id]) setRationale('[data-rationale="backup-' + i + '"]', rationales[b.cake.id]);
      });
    }

    /* ---- GA4 ---- */
    sendGA4(name, params) {
      const payload = Object.assign(
        {
          quiz_name: 'nesuto_cake_finder',
          quiz_version: '4.0_persona',
          quiz_type: 'personality_recommendation',
          page_location: window.location.href,
          page_title: document.title,
          timestamp: new Date().toISOString()
        },
        params || {}
      );
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, payload);
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event: name }, payload));
      }
    }

    trackShop(cake, sizeObj, kind) {
      if (!cake) return;
      const value = (sizeObj ? sizeObj.price : cake.price) / 100;
      const item = {
        item_id: cake.id,
        item_name: cake.name,
        item_category: 'Cakes',
        item_variant: sizeObj && sizeObj.name ? sizeObj.name : 'default',
        price: value,
        quantity: 1
      };
      this.sendGA4('select_item', {
        item_list_id: 'quiz_recommendations',
        item_list_name: 'Quiz Recommendations',
        items: [item],
        click_type: kind
      });
      this.sendGA4('add_to_cart', { currency: 'SGD', value: value, items: [item], source: 'quiz_recommendation' });
      try {
        sessionStorage.setItem('nesuto_quiz_referral', JSON.stringify({
          cakeId: cake.id,
          cakeName: cake.name,
          size: item.item_variant,
          persona: this.lastResult && this.lastResult.persona ? this.lastResult.persona.slug : null,
          timestamp: Date.now(),
          kind: kind
        }));
      } catch (e) { /* storage may be unavailable */ }
    }

    initReferralTracking() {
      let referral;
      try {
        referral = JSON.parse(sessionStorage.getItem('nesuto_quiz_referral') || 'null');
      } catch (e) { referral = null; }
      if (!referral) return;
      this.sendGA4('page_view_from_quiz', {
        source: 'quiz_recommendation',
        recommended_cake: referral.cakeName,
        persona: referral.persona || 'none',
        time_since_quiz: Date.now() - referral.timestamp
      });
    }
  }

  if (!customElements.get('cake-quiz')) {
    customElements.define('cake-quiz', CakeQuiz);
  }
})();
